import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { AI_PLAN_EVENT, AI_PLANS_PAGE, aiPlanMessage } from './aiPlanGate';

/**
 * App-wide « this is in another formula » modal. Mounted once (App.jsx); opened by the API
 * client when an AI action answers ai_tier_required (403) or « included questions used » (402).
 */
export default function AiPlanGateModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wall, setWall] = useState(null);

  useEffect(() => {
    const onWall = (e) => setWall(e.detail || null);
    window.addEventListener(AI_PLAN_EVENT, onWall);
    return () => window.removeEventListener(AI_PLAN_EVENT, onWall);
  }, []);

  useEffect(() => {
    if (!wall) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setWall(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wall]);

  if (!wall) return null;
  const close = () => setWall(null);
  const goToPlans = () => { setWall(null); navigate(AI_PLANS_PAGE); };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-plan-gate-title"
      data-testid="ai-plan-gate"
      onClick={close}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={close} aria-label={t('Close')}
          className="absolute end-3 top-3 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800">
          <X size={18} />
        </button>
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
          <Sparkles size={22} />
        </span>
        <h3 id="ai-plan-gate-title" className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100" dir="auto">
          {aiPlanMessage(wall, t)}
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" dir="auto">
          {wall.kind === 'tier'
            ? t('Choose this plan in AI Tokens and you can use it right away.')
            : t('The night analyses continue. Only new questions need a plan.')}
        </p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button type="button" onClick={close}
            className="rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
            {t('Not now')}
          </button>
          <button type="button" autoFocus onClick={goToPlans} data-testid="ai-plan-gate-go"
            className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">
            {t('See the plans')}
          </button>
        </div>
      </div>
    </div>
  );
}
